import {
  type CustomEditor,
  type CustomElement,
  type CustomLeaf,
  type CustomSlateNode,
  extendedNodeTypes,
  getInitialValue,
  stylesAttributes,
} from '@mint-lib/tw-components';
import DOMPurify from 'dompurify';
import { BaseText, Editor, Element, Node, Text, Transforms } from 'slate';

export const withNormalization = (editor: Editor) => {
  const { normalizeNode } = editor;

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    // Remove empty list items
    if (Element.isElementType(node, 'list-item')) {
      const isEmpty = editor.children.every(
        (child) => Node.string(child).trim() === '',
      );

      const text = Node.string(node);
      if (text.trim() === '' && isEmpty) {
        Transforms.removeNodes(editor, { at: path });
        editor.children = getInitialValue();
        return;
      }
    }

    // Call the original `normalizeNode` to handle other cases
    normalizeNode(entry);
  };

  return editor as CustomEditor;
};

const serializeTextNode = (node: CustomSlateNode, type?: string) => {
  const textNode = node as BaseText & CustomLeaf;
  let string = DOMPurify.sanitize(textNode.text);
  // Loop through each style attribute and wrap the text in the corresponding HTML tag
  Object.keys(stylesAttributes).forEach((style) => {
    const isAttribute = textNode[style as keyof CustomLeaf];

    if (isAttribute) {
      const assignedStyle =
        stylesAttributes[style as keyof typeof stylesAttributes];
      string = `<${assignedStyle}>${string}</${assignedStyle}>`;
    }
  });
  // If the type includes 'list', wrap the text in <li> tags
  return type?.includes('list') ? `<li>${string}</li>` : string;
};

const serializeElement = (node: CustomSlateNode) => {
  const element = node as CustomElement;
  const children = element.children
    .map((n) => serializeSlateToHtml(n as CustomSlateNode, node.type))
    .join('\n');

  // prevent adding empty tags to editor
  if (!children) {
    return '';
  }
  const sanitizedChildren = DOMPurify.sanitize(children, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'a',
      'em',
      'del',
      'u',
      'strong',
      'br',
      'hr',
      'p',
    ],
    ALLOWED_URI_REGEXP: /^(https?|mailto):/, // for safe links
  });

  const tag =
    extendedNodeTypes[
      (element as CustomSlateNode).type as keyof typeof extendedNodeTypes
    ];

  // prevent tags with empty content
  const trimmedElement = DOMPurify.sanitize(sanitizedChildren, {
    ALLOWED_TAGS: [],
  }).trim();

  return tag && trimmedElement
    ? `<${tag}>${sanitizedChildren}</${tag}>`
    : trimmedElement;
};

export const serializeSlateToHtml = (
  node: CustomSlateNode,
  type?: string,
): string => {
  if (Text.isText(node)) {
    return serializeTextNode(node, type);
  }

  return serializeElement(node);
};
